'use client';

import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import ErrorLabel from '@/components/ErrorLabel';
import InputLabel from '@/components/InputLabel';

import { IconDown16 } from '@cineamo/ui/src/icons/ic-down';

export type BasicDropdownOption<TValue = string | number> = {
    value: TValue;
    label?: string | React.JSX.Element;
    isSelectable?: boolean;
};

export enum BasicDropdownStyle {
    default = 'default',
    light = 'light',
    dropdown_on_white_bg = 'dropdown_on_white_bg',
    bg_white = 'bg_white'
}

export type BasicDropdownStyleConfig = {
    itemBgColorClass?: string;
    listBgColorClass?: string;
    selectedItemClassName?: string;
    style?: BasicDropdownStyle;
};

export type BasicDropdownProps<TValue = string | number> = {
    className?: string;
    itemClassName?: string;
    style?: BasicDropdownStyle | BasicDropdownStyleConfig;
    css?: {
        selectedItemBackgroundColor?: string;
        selectedItemForegroundColor?: string;
        listBackgroundColor?: string;
        selectableItemBackgroundColor?: string;
        selectableItemHoverBackgroundColor?: string;
        selectableItemForegroundColor?: string;
    };
    height?: 'default-48' | 'custom';
    label?: string;
    placeholder?: string | React.JSX.Element;
    preselectedIndex?: number;
    options: BasicDropdownOption<TValue>[];
    onChange?: (selectedItem: BasicDropdownOption<TValue>, index: number) => void;
    onClose?: () => void;
    customIcon?: React.JSX.Element;
    disabled?: boolean;
    isDropdownAbsolutePositioned?: boolean;
    error?: string;
    openingDirection?: 'up' | 'down';
    required?: boolean;
    paginationRef?: (node?: Element) => void;
};

function BasicDropdown<TValue>(props: BasicDropdownProps<TValue>): React.JSX.Element {
    const {
        className,
        itemClassName,
        style,
        css,
        height,
        label,
        preselectedIndex,
        options,
        onChange,
        onClose,
        customIcon,
        disabled,
        isDropdownAbsolutePositioned,
        placeholder,
        error,
        openingDirection,
        paginationRef
    } = props;

    const dropdownRef = useRef(null);

    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<number>(
        placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0
    );
    const [focused, setFocused] = useState(false);

    function handleClickOutside(event: Event) {
        if (!dropdownRef?.current?.contains(event.target)) {
            setIsDropdownOpen(false);
        }
    }

    useEffect(() => {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Update the current selected option if the preselectedIndex changes.
    useEffect(() => {
        setSelectedOption(placeholder && preselectedIndex === undefined ? -1 : preselectedIndex ? preselectedIndex : 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preselectedIndex]);

    useEffect(() => {
        if (!isDropdownOpen && onClose) {
            onClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDropdownOpen]);

    let labelColor = 'text-gray-transparent';
    let focusedLabelColor = 'text-cyan';
    let selectedItemBackgroundColor: string;
    let itemTextClassname: string;
    let listBackgroundColor: string;
    let selectableItemBackgroundColor: string;

    switch (style && typeof style !== 'string' ? style.style : style) {
        case BasicDropdownStyle.dropdown_on_white_bg:
            labelColor = 'text-darkBlue-transparent';
            focusedLabelColor = 'text-darkBlue';
            selectedItemBackgroundColor = classnames(
                'bg-gray-light hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style
                    ? style.listBgColorClass
                    : 'bg-darkBlue/20'
            );
            selectableItemBackgroundColor = classnames(
                'bg-gray-light hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style ? style.itemBgColorClass : ''
            );
            break;
        case BasicDropdownStyle.light:
            labelColor = 'text-darkBlue-transparent';
            focusedLabelColor = 'text-darkBlue';
            selectedItemBackgroundColor = classnames(
                'hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : ''
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style
                    ? style.listBgColorClass
                    : 'bg-white/80'
            );
            selectableItemBackgroundColor = classnames(
                'hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style ? style.itemBgColorClass : ''
            );
            break;
        case BasicDropdownStyle.bg_white:
            selectedItemBackgroundColor = classnames(
                'bg-darkBlue/20 hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : 'bg-darkBlue/20'
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style ? style.listBgColorClass : 'bg-white'
            );
            itemTextClassname = 'text-darkBlue text-body-14 font-medium';
            selectableItemBackgroundColor = classnames(
                'active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style
                    ? style.itemBgColorClass
                    : 'hover:bg-gray/50'
            );
            break;
        default:
            selectedItemBackgroundColor = classnames(
                'bg-white/20 hover:bg-cyan/20 active:opacity-80',
                style && typeof style !== 'string' && 'selectedItemClassName' in style
                    ? style.selectedItemClassName
                    : 'bg-white/20'
            );
            listBackgroundColor = classnames(
                style && typeof style !== 'string' && 'listBgColorClass' in style
                    ? style.listBgColorClass
                    : 'bg-white/20'
            );
            selectableItemBackgroundColor = classnames(
                'active:opacity-80',
                style && typeof style !== 'string' && 'itemBgColorClass' in style
                    ? style.itemBgColorClass
                    : 'bg-white/20 hover:bg-cyan/20'
            );
            break;
    }

    function onDropdownOpenClickHandler() {
        setIsDropdownOpen(!isDropdownOpen);
    }

    function enterFocus() {
        if (disabled !== true) {
            setFocused(true);
        }
    }

    function exitFocus() {
        setFocused(false);
    }

    function onChangeInternal(option: BasicDropdownOption<TValue>, index: number) {
        setSelectedOption(index);
        setIsDropdownOpen(false);
        if (onChange) {
            onChange(option, index);
        }
    }

    return (
        <div
            className={classnames(className, 'w-full flex flex-col')}
            onFocus={enterFocus}
            onBlur={exitFocus}
            ref={dropdownRef}>
            {label && (
                <InputLabel
                    label={label}
                    labelStyle={classnames(
                        { 'opacity-100': focused },
                        { [focusedLabelColor]: focused },
                        { [labelColor]: !focused }
                    )}
                    requiredLabel={props.required}
                />
            )}
            <div
                className={classnames('relative', {
                    'h-48': height !== 'custom' && isDropdownAbsolutePositioned !== false,
                    'h-full': height === 'custom' || isDropdownAbsolutePositioned
                })}>
                <button
                    className={classnames(
                        'w-full flex flex-row items-center justify-between h-full select-none space-x-12',
                        'transition-colors duration-100',
                        'active:opacity-80 cursor-pointer',
                        selectedItemBackgroundColor,
                        {
                            'px-12 py-6': !(
                                options?.[selectedOption]?.label && React.isValidElement(options[selectedOption].label)
                            ),
                            'rounded-t-4': openingDirection !== 'up' || (!isDropdownOpen && openingDirection === 'up'),
                            'rounded-b-4': (!isDropdownOpen && openingDirection !== 'up') || openingDirection === 'up'
                        }
                    )}
                    style={
                        css
                            ? {
                                  backgroundColor: css.selectedItemBackgroundColor,
                                  color: css.selectedItemForegroundColor
                              }
                            : undefined
                    }
                    onClick={onDropdownOpenClickHandler}>
                    {selectedOption === -1 ? (
                        <span>{placeholder}</span>
                    ) : (
                        <span>
                            {options?.[selectedOption]?.isSelectable !== false &&
                                (options?.[selectedOption]?.label ?? '-')}
                        </span>
                    )}
                    {customIcon || (
                        <div>
                            <IconDown16
                                className={classnames('transition-all duration-100 flex-none', {
                                    'mr-12':
                                        options?.[selectedOption]?.label &&
                                        React.isValidElement(options[selectedOption].label),
                                    'rotate-180': isDropdownOpen
                                })}
                            />
                        </div>
                    )}
                </button>
                {isDropdownOpen && (
                    <div
                        className={classnames('w-full h-1', listBackgroundColor, {
                            absolute: isDropdownAbsolutePositioned !== false,
                            'bottom-full': openingDirection === 'up'
                        })}
                        style={{
                            backgroundColor: listBackgroundColor
                        }}
                    />
                )}
                {isDropdownOpen && (
                    <div
                        className={classnames(
                            'w-full overflow-hidden z-modal-view',
                            {
                                absolute: isDropdownAbsolutePositioned !== false,
                                'bottom-full rounded-t-4': openingDirection === 'up',
                                'top-full rounded-b-4': openingDirection !== 'up'
                            },
                            listBackgroundColor
                        )}
                        style={{
                            backgroundColor: listBackgroundColor
                        }}>
                        <div className="overflow-auto max-h-480">
                            {options?.map((it, index) => (
                                <button
                                    key={`dropdownItem#${index}`}
                                    className={classnames(
                                        'relative w-full group',
                                        'transition-all duration-100',
                                        {
                                            'px-12 py-6': !React.isValidElement(it.label)
                                        },
                                        {
                                            'cursor-pointer': it.isSelectable !== false
                                        },
                                        it.isSelectable !== false && selectableItemBackgroundColor,
                                        itemClassName,
                                        itemTextClassname
                                    )}
                                    style={
                                        css
                                            ? {
                                                  backgroundColor: css.selectableItemBackgroundColor,
                                                  color: css.selectableItemForegroundColor
                                              }
                                            : undefined
                                    }
                                    onClick={() => {
                                        if (it.isSelectable !== false) {
                                            onChangeInternal(it, index);
                                        }
                                    }}>
                                    {css?.selectableItemHoverBackgroundColor && (
                                        <div className="absolute left-0 top-0 w-full h-full z-10">
                                            <div className="w-full h-full opacity-0 group-hover:opacity-20">
                                                <div
                                                    className="w-full h-full"
                                                    style={{
                                                        backgroundColor: css.selectableItemHoverBackgroundColor
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    )}
                                    {it.label ?? '-'}
                                </button>
                            ))}
                            {paginationRef && <div ref={paginationRef} />}
                        </div>
                    </div>
                )}
            </div>
            {error && <ErrorLabel error={error} />}
        </div>
    );
}

export default BasicDropdown;
